import type { HttpError } from '@refinedev/core'
import { CanAccess, useList, useShow } from '@refinedev/core'
import type { Cabinet, Location } from '@sirius/contracts/admin'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { ConfirmDialog } from '~/components/confirmDialog'
import { ShowView, ShowViewHeader } from '~/components/refineUi/views/showView'
import { Button } from '~/components/ui/button'
import { formatDateTime } from '~/i18n/format'
import { History } from '~/pages/activity/history'
import { NotFoundPage } from '~/pages/notFound'
import { useDeleteLocation } from './locationDelete'

export function LocationShow() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const deleteLocation = useDeleteLocation()
  const { query } = useShow<Location, HttpError>({ resource: 'locations' })
  const location = query.data?.data
  const cabinets = useList<Cabinet, HttpError>({
    resource: 'cabinets',
    pagination: { currentPage: 1, pageSize: 100 },
    sorters: [{ field: 'name', order: 'asc' }],
    filters: [{ field: 'locationId', operator: 'eq', value: location?.id }],
    queryOptions: { enabled: location !== undefined },
  })

  // unknown while the list loads or failed: no misleading "(0)", no delete
  const cabinetCount = cabinets.query.isSuccess
    ? cabinets.result.total
    : undefined

  if (query.error?.statusCode === 404) {
    return <NotFoundPage />
  }

  return (
    <ShowView>
      <ShowViewHeader title={location?.name} />
      {query.error && (
        <p
          role="alert"
          className="text-destructive text-sm"
        >
          {t('locationsPage.show.loadError', { message: query.error.message })}
        </p>
      )}
      {location && (
        <div className="space-y-6">
          <dl className="grid max-w-md grid-cols-[8rem_1fr] gap-2 text-sm">
            <dt className="text-muted-foreground">
              {t('locationsPage.show.name')}
            </dt>
            <dd>{location.name}</dd>
            <dt className="text-muted-foreground">
              {t('locationsPage.show.created')}
            </dt>
            <dd>{formatDateTime(location.createdAt)}</dd>
            <dt className="text-muted-foreground">
              {t('locationsPage.show.updated')}
            </dt>
            <dd>{formatDateTime(location.updatedAt)}</dd>
          </dl>
          <section>
            <h3 className="mb-2 font-semibold">
              {cabinetCount === undefined
                ? t('locationsPage.show.cabinets')
                : t('locationsPage.show.cabinetsWithTotal', {
                    total: cabinetCount,
                  })}
            </h3>
            {cabinets.query.isError && (
              <p
                role="alert"
                className="text-destructive text-sm"
              >
                {t('locationsPage.show.cabinetsLoadError', {
                  message: cabinets.query.error.message,
                })}
              </p>
            )}
            {cabinets.query.isPending && !cabinets.query.isError && (
              <p className="text-muted-foreground text-sm">
                {t('locationsPage.show.cabinetsLoading')}
              </p>
            )}
            <ul className="space-y-1 text-sm">
              {cabinets.result.data.map((cabinet) => (
                <li key={cabinet.id}>
                  <Link
                    className="underline"
                    to={`/cabinets/${cabinet.id}`}
                  >
                    {cabinet.name}
                  </Link>{' '}
                  · {cabinet.status}
                </li>
              ))}
            </ul>
          </section>
          <History
            record={{
              entity: 'location',
              id: location.id,
              tenantId: location.tenantId,
            }}
          />
          {/* a technician only reads locations */}
          <CanAccess
            resource="locations"
            action="delete"
          >
            <ConfirmDialog
              trigger={
                <Button
                  variant="destructive"
                  disabled={cabinetCount === undefined}
                >
                  {t('locationsPage.show.deleteButton')}
                </Button>
              }
              title={t('locationsPage.show.deleteTitle', {
                name: location.name,
              })}
              description={t('locationsPage.show.deleteDescription')}
              confirmLabel={t('locationsPage.show.delete')}
              destructive
              onConfirm={() =>
                deleteLocation(location.id, () => {
                  void navigate('/locations')
                })
              }
            />
          </CanAccess>
        </div>
      )}
    </ShowView>
  )
}
